import React, { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useApp } from "./AppContext";

const DEFAULT_PAYMENT_FLAGS = Object.freeze({
  pix: false,
  card: true,
  points: true,
});

function parsePaymentFlags(features) {
  if (!features || typeof features !== "object") return DEFAULT_PAYMENT_FLAGS;

  const raw = features.payments;
  if (!raw) return DEFAULT_PAYMENT_FLAGS;

  let parsed = null;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (_) {
      return DEFAULT_PAYMENT_FLAGS;
    }
  } else if (typeof raw === "object") {
    parsed = raw;
  } else {
    return DEFAULT_PAYMENT_FLAGS;
  }

  const flags = { ...DEFAULT_PAYMENT_FLAGS, ...parsed };

  if (!flags.pix && !flags.card && !flags.points) {
    console.warn(
      "All payment methods disabled in features.payments — falling back to defaults",
    );
    return DEFAULT_PAYMENT_FLAGS;
  }

  return flags;
}

const FeatureFlagContext = createContext({
  features: null,
  isEnabled: () => true,
  paymentFlags: DEFAULT_PAYMENT_FLAGS,
});

function normalizeFlagKey(flag) {
  if (typeof flag !== "string") return "";
  return flag.trim();
}

export function isFeatureEnabled(features, flag) {
  const key = normalizeFlagKey(flag);
  if (!key) return true;

  if (!features || typeof features !== "object") return true;

  if (features.is_active === false && key !== "is_active") return false;

  if (!(key in features)) return true;

  return Boolean(features[key]);
}

export function FeatureFlagProvider({ children }) {
  const { whitelabel } = useApp();

  const features = whitelabel?.features ?? null;

  const value = useMemo(() => {
    return {
      features,
      isEnabled: (flag) => isFeatureEnabled(features, flag),
      paymentFlags: parsePaymentFlags(features),
    };
  }, [features]);

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

FeatureFlagProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useFeatureFlag(flag) {
  const ctx = useContext(FeatureFlagContext);
  return ctx.isEnabled(flag);
}

export function usePaymentFlags() {
  const ctx = useContext(FeatureFlagContext);
  return ctx.paymentFlags;
}
